// =====================================================================
// src/lib/quadro.js — o Quadro de Reservas (quarto × dia), sem DOM
//
// Cópia da conta de `renderQuadro` do Monteiro de Moraes (index.html,
// 4874) e dos navegadores `quadroNavAno/Mes/Dia/IrHoje` (4768–4872). A
// tela (`src/modulos/reservas.js`, aba Quadro) copia a marcação: a barra
// azul de navegação, o cabeçalho de dias, a coluna fixa à esquerda e a
// célula colorida pela plataforma.
//
// Divergências de domínio, todas obrigatórias:
//   · lá as linhas se agrupam por PROPRIETÁRIO. Para a imobiliária
//     isso vale igual (os flats dela têm dono); para o hotel os 456 quartos
//     não têm dono — agrupam-se por ANDAR, como na Disponibilidade;
//   · conflito e "sobreposta": desde a 0050 a IMOBILIÁRIA pode ter duas
//     confirmadas no mesmo flat com datas que se cruzam (o hotel não — a
//     exclusão da 0015 ficou só para ele). A célula segue `getResForDay`
//     (4938): a primeira reserva, `conflito` quando há mais de uma,
//     `sobreposta` quando todas as outras estão marcadas mutuamente com a
//     primeira (`sobreposta_com`, marcar_sobreposicao);
//   · "manutenção" (plataforma `outro`) não é reserva aqui — manutenção é
//     chamado (0029). Berço, banheira e garagem paga também não;
//   · o valor por noite só sai para quem tem `financeiro` (INVARIANTE 2);
//   · cancelada e no-show não pintam a célula.
// =====================================================================
import { somarDias, noites, quartosParaReserva, fimDaOcupacao, ocupaANoite } from './reservas.js';

export const MESES = Object.freeze(['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']);
export const MESES_CURTO = Object.freeze(['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']);
export const DIAS_SEMANA = Object.freeze(['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']);

const p2 = n => String(n).padStart(2, '0');
export function diasNoMes(ano, mes) { return new Date(Number(ano), Number(mes), 0).getDate(); }

// O estado da navegação, como `_qState`: ano e mês como strings ('2026',
// '09'), diaIni/diaFim como '01'..'31'. `inicial(hoje)` é o de `quadroIrHoje`:
// o mês de hoje inteiro.
export function inicial(hoje) {
  const [ano, mes] = hoje.split('-');
  return { ano, mes, diaIni: '01', diaFim: p2(diasNoMes(ano, mes)) };
}

// Os dias da janela, em ISO (renderQuadro, 4914). Desktop: do diaIni ao
// diaFim do mês. Celular (`e.inicioMovel`, 4906): SETE dias absolutos a
// partir de uma data, atravessando o mês se preciso.
export function diasDaJanela(e) {
  if (e.de) return Array.from({ length: noites(e.de, e.ate) + 1 }, (_, i) => somarDias(e.de, i));
  if (e.inicioMovel) return Array.from({ length: 7 }, (_, i) => somarDias(e.inicioMovel, i));
  const ini = Number(e.diaIni), fim = Number(e.diaFim);
  return Array.from({ length: Math.max(0, fim - ini + 1) }, (_, i) => `${e.ano}-${e.mes}-${p2(ini + i)}`);
}

// ── o celular: janela de 7 dias (`_qIsMobile`, 4765; 4885–4900) ──────
// Lá a tela ≤ 768px encolhe o quadro para 7 dias a partir de HOJE, e a
// navegação passa a ser por semana (‹ 7d · 7d ›), cruzando meses. Ano e
// mês da barra acompanham o começo da janela (4832).
export function janelaMovel(inicio) {
  const [ano, mes, dia] = inicio.split('-');
  const fim = somarDias(inicio, 6);
  return { ano, mes, diaIni: dia, diaFim: fim.slice(0, 7) === inicio.slice(0, 7) ? fim.slice(8, 10) : p2(diasNoMes(ano, mes)), inicioMovel: inicio };
}
// `quadroNavSemana` (4826): pula 7 dias
export function navegarSemana(e, d) { return janelaMovel(somarDias(e.inicioMovel, d * 7)); }
// `quadroNavDia` no celular (4791): um dia, mantendo a janela de 7
export function navegarDiaMovel(e, d) { return janelaMovel(somarDias(e.inicioMovel, d)); }
// Entre desktop e celular, mantendo o que a pessoa estava olhando (4891).
export function paraMovel(e) { return e.inicioMovel ? e : janelaMovel(`${e.ano}-${e.mes}-${e.diaIni}`); }
export function paraDesktop(e) { if (!e.inicioMovel) return e; const { inicioMovel, ...r } = e; return { ...r, diaIni: '01', diaFim: p2(diasNoMes(r.ano, r.mes)) }; }
// O intervalo [ini, fim) que a busca do banco precisa cobrir para a janela.
// No desktop cobre o MÊS inteiro (deslizar os dias não busca de novo);
// no celular, os 7 dias.
export function intervaloDaJanela(e) {
  const dias = (e.inicioMovel || e.de) ? diasDaJanela(e) : diasDaJanela({ ...e, diaIni: '01', diaFim: p2(diasNoMes(e.ano, e.mes)) });
  return dias.length ? { ini: dias[0], fim: somarDias(dias[dias.length - 1], 1), chave: `${dias[0]}..${dias[dias.length - 1]}` } : { ini: null, fim: null, chave: '' };
}

// `quadroNavAno`: muda o ano, mantém mês e janela (recortada ao mês novo —
// 29/02 não existe no ano seguinte).
export function navegarAno(e, d) {
  const ano = String(Number(e.ano) + d);
  const dim = diasNoMes(ano, e.mes);
  // saindo de um período livre: o mês dele, inteiro, no ano novo
  if (e.de) return { ano, mes: e.mes, diaIni: '01', diaFim: p2(dim) };
  // celular (4772): o mesmo dia/mês no ano novo
  if (e.inicioMovel) return janelaMovel(`${ano}-${e.mes}-${p2(Math.min(Number(e.inicioMovel.slice(8, 10)), dim))}`);
  return { ...e, ano, diaIni: p2(Math.min(Number(e.diaIni), dim)), diaFim: p2(Math.min(Number(e.diaFim), dim)) };
}
// `quadroNavMes`: mês anterior/seguinte, o mês inteiro.
export function navegarMes(e, d) {
  let ano = Number(e.ano), mes = Number(e.mes) + d;
  if (mes < 1) { mes = 12; ano--; } if (mes > 12) { mes = 1; ano++; }
  // celular (4784): pula para o dia 1 do mês novo
  if (e.inicioMovel) return janelaMovel(`${ano}-${p2(mes)}-01`);
  return { ano: String(ano), mes: p2(mes), diaIni: '01', diaFim: p2(diasNoMes(ano, mes)) };
}
// `quadroNavDia` (desktop): desliza a janela dentro do mês, sem mudar o tamanho.
export function navegarDias(e, d) {
  // período livre: desliza inteiro, atravessando meses
  if (e.de) return periodo(somarDias(e.de, d), somarDias(e.ate, d));
  const dim = diasNoMes(e.ano, e.mes);
  let ini = Number(e.diaIni), fim = Number(e.diaFim);
  const janela = fim - ini;
  ini += d; fim += d;
  if (ini < 1) { ini = 1; fim = janela + 1; }
  if (fim > dim) { fim = dim; ini = dim - janela; }
  if (ini < 1) ini = 1;
  return { ...e, diaIni: p2(ini), diaFim: p2(fim) };
}

// ── o PERÍODO LIVRE (Hugo, 23/09/2026) ──────────────────────────────
// "opção de selecionar período na parte superior esquerda, com opção de
// clicar no calendário". Não existe no MdM: lá a janela é sempre um
// recorte do mês. Aqui "De" e "Até" são datas quaisquer, atravessando
// meses; ano e mês da barra passam a ser os do começo. O teto de 92 dias
// (um trimestre) existe porque cada dia é uma coluna de 48px: 456 quartos
// × um ano inteiro é uma tabela que o navegador não desenha com dignidade.
export const PERIODO_MAXIMO = 92;
export function validarPeriodo(de, ate) {
  if (!de || !ate) return 'Informe as duas datas do período';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(de) || !/^\d{4}-\d{2}-\d{2}$/.test(ate)) return 'Data inválida';
  if (ate < de) return 'A data final é anterior à inicial';
  if (noites(de, ate) + 1 > PERIODO_MAXIMO) return `O período pode ter até ${PERIODO_MAXIMO} dias`;
  return null;
}
export function periodo(de, ate) {
  const [ano, mes, dia] = de.split('-');
  return { ano, mes, diaIni: dia, diaFim: ate.slice(0, 7) === de.slice(0, 7) ? ate.slice(8, 10) : p2(diasNoMes(ano, mes)), de, ate };
}
// O que a barra escreve entre ‹ ›: "01 — 30" no mês; "25/09 — 10/10" no período.
export function rotuloDosDias(e) {
  if (e.de) return `${e.de.slice(8, 10)}/${e.de.slice(5, 7)} — ${e.ate.slice(8, 10)}/${e.ate.slice(5, 7)}`;
  return `${e.diaIni} — ${e.diaFim}`;
}
// O primeiro e o último dia que se vê — o que os dois calendários mostram.
export function limitesDaJanela(e) {
  const d = diasDaJanela(e);
  return { de: d[0] || null, ate: d[d.length - 1] || null };
}

// O cabeçalho de um dia (4954): nome curto, número, se é fim de semana,
// se é hoje, e o mês quando ele muda dentro da janela.
export function cabecalhoDoDia(iso, hoje, mesAnterior) {
  const d = new Date(iso + 'T12:00:00');
  const dow = d.getDay();
  const mes = Number(iso.slice(5, 7));
  return { iso, nome: DIAS_SEMANA[dow], dia: iso.slice(8, 10), fimDeSemana: dow === 0 || dow === 6, hoje: iso === hoje,
    mostraMes: mes !== mesAnterior, mesCurto: MESES_CURTO[mes - 1], mes };
}

// A reserva que ocupa o quarto naquele dia (`getResForDay`, 4938), ou
// null. Com mais de uma (0050), a primeira — `estadoDoDia` diz o resto.
export function reservaDoDia(reservas, unidadeId, iso) {
  return (reservas || []).find(r => r.unidade_id === unidadeId && ocupaANoite(r, iso)) || null;   // 0096
}

// `_saoSobrepostas` (6968): marcação MÚTUA — as duas apontam uma para a outra.
export function saoSobrepostas(a, b) {
  if (!a || !b) return false;
  return (a.sobreposta_com || []).includes(b.id) && (b.sobreposta_com || []).includes(a.id);
}
// `getResForDay` (4938) sobre a lista das reservas de um dia: a primeira,
// se há conflito, se o conflito é sobreposição intencional, e a segunda.
export function estadoDoDia(todas) {
  if (!todas || !todas.length) return null;
  const res = todas[0];
  const conflito = todas.length > 1;
  const sobreposta = conflito && todas.slice(1).every(r => saoSobrepostas(res, r));
  return { res, conflito: conflito && !sobreposta, sobreposta, extra: todas[1] || null };
}

// Um índice por quarto e dia, para a tela não varrer a lista 13.680
// vezes (456 quartos × 30 dias): `indice.get(unidade_id).get(iso)` é a
// LISTA das reservas daquele dia (uma, ou mais de uma desde a 0050), na
// ordem de check-in — `estadoDoDia` lê a lista.
export function indexarReservas(reservas, dias) {
  const idx = new Map();
  const set = new Set(dias);
  const ordem = (reservas || []).slice().sort((a, b) => String(a.check_in).localeCompare(String(b.check_in)));
  for (const r of ordem) {
    if (r.situacao === 'cancelada' || r.situacao === 'noshow' || !r.check_in || !r.check_out) continue;
    if (!idx.has(r.unidade_id)) idx.set(r.unidade_id, new Map());
    const m = idx.get(r.unidade_id);
    const fim = fimDaOcupacao(r);   // 0096: a saída real, se foi antes
    for (let d = r.check_in; d < fim; d = somarDias(d, 1)) if (set.has(d)) { if (!m.has(d)) m.set(d, []); m.get(d).push(r); }
  }
  return idx;
}

// Os grupos de linhas. Dono: por proprietário, na ordem do nome (4974 —
// lá `_proprietarios`; aqui `unidades.proprietarios.nome`), "Outros" no
// fim para quarto sem dono. Hotel: por andar, o agrupamento da modal.
export function gruposDoQuadro(unidades, visao) {
  const ativas = (unidades || []).filter(u => u.ativo !== false);
  if (visao !== 'dono') return quartosParaReserva(ativas).map(a => ({ rotulo: a.rotulo, quartos: a.quartos }));
  const m = new Map();
  for (const u of ativas) {
    const p = (u.proprietarios && u.proprietarios.nome) || 'Outros';
    if (!m.has(p)) m.set(p, []);
    m.get(p).push(u);
  }
  const ordem = [...m.keys()].sort((a, b) => a === 'Outros' ? 1 : b === 'Outros' ? -1 : a.localeCompare(b));
  return ordem.map(p => ({ rotulo: p, quartos: m.get(p).slice().sort((a, b) => String(a.numero).localeCompare(String(b.numero), undefined, { numeric: true })) }));
}

// "R$123" por noite (4995): só para quem tem o valor.
export function valorPorNoite(valorTotal, r) {
  const n = Math.max(1, noites(r.check_in, r.check_out));
  const v = Number(valorTotal);
  if (!v || Number.isNaN(v)) return null;
  return Math.round(v / n);
}

// A legenda da barra (5083): as plataformas com cor própria e o resto.
// A legenda (5083): as plataformas e, desde a 0050, os dois estados do MdM
// — Conflito (duas cores na diagonal) e Sobreposta (verde e laranja).
export const LEGENDA = Object.freeze([
  { rotulo: 'Direto', cor: '#16a34a' }, { rotulo: 'Airbnb', cor: '#dc2626' }, { rotulo: 'Booking', cor: '#2563eb' }, { rotulo: 'Outra', cor: '#6b7280' },
  { rotulo: 'Conflito', cor: 'linear-gradient(135deg,#dc2626 50%,#2563eb 50%)' },
  { rotulo: 'Sobreposta', cor: 'linear-gradient(135deg,#16a34a 50%,#f59e0b 50%)' },
]);
